import { useState, useEffect } from "react";
import Head from "next/head";
import Image from 'next/image';
import styles from "../styles/pages/Memorial.module.scss";

// Cookie functions
const getMemorialCookie = () => {
  if (typeof window === 'undefined') return null;
  const cookieName = 'have_seen_memorial';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      // Parse JSON value to get language and status
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }
  return null;
};

const setMemorialCookie = (lang) => {
  if (typeof window === 'undefined') return;
  const cookieName = 'have_seen_memorial';
  const cookieValue = JSON.stringify({ lang, seen: true, timestamp: Date.now() });
  const expires = new Date();
  // expires.setTime(expires.getTime() + (1 * 60 * 1000)); // 1 minute for testing
  expires.setTime(expires.getTime() + (1 * 60 * 60 * 1000)); // 1 hour
  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)};expires=${expires.toUTCString()};path=/`;
};

// Hook to detect mobile device
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Handle SSR case
    if (typeof window === 'undefined') return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};


export default function MemorialPage() {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkMemorialCookie = () => {
      const memorialData = getMemorialCookie();
      // Check if user has seen memorial for ANY language
      if (memorialData && memorialData.seen === true) {
        // Redirect to appropriate homepage based on current URL language
        const redirectUrl = memorialData?.lang === 'th' ? `/th` : '/';
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 Redirecting to: ${redirectUrl} (user has seen memorial in ${memorialData.lang})`);
        }
        window.location.href = redirectUrl;
        return;
      }
    };

    // Check cookie on page load
    checkMemorialCookie();

    // Debug: Log cookie status in development
    if (process.env.NODE_ENV === 'development') {
      const memorialData = getMemorialCookie();
      console.log('🍪 Memorial Cookie Status:');
      if (memorialData) {
        console.log(`- Language: ${memorialData.lang}`);
        console.log(`- Seen: ${memorialData.seen ? '✅ Yes' : '❌ No'}`);
        console.log(`- Timestamp: ${new Date(memorialData.timestamp).toLocaleString()}`);
      } else {
        console.log('- No memorial cookie found');
      }
    }

    // Add memorial-page class to body to exclude global filters
    document.body.classList.add('memorial-page');

    // Add global styles directly to body when component mounts
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.overflowX = 'hidden';
    document.body.style.background = '#000';

    // Also set html styles
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.width = '100%';
    document.documentElement.style.height = '100%';
    document.documentElement.style.background = '#000';

    const timer = setTimeout(() => setIsLoading(false), 500);

    // Clean up when component unmounts
    return () => {
      // Remove memorial-page class
      document.body.classList.remove('memorial-page');

      // Reset body styles
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overflowX = '';
      document.body.style.background = '';

      // Reset html styles
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
      document.documentElement.style.background = '';
      clearTimeout(timer);
    };
  }, [isMobile]);

  // Simple client-side loading delay
  // useEffect(() => {
  //   // Only run on client side
  //   if (typeof window === 'undefined') return;

  //   // Just wait a short time for the page to load, then hide loading
  //   const timer = setTimeout(() => setIsLoading(false), 400);

  //   return () => clearTimeout(timer);
  // }, []);

  const handleEnterSiteTH = () => {
    // Set cookie with Thai language info
    setMemorialCookie('th');
    // Redirect to Thai site
    window.location.href = "/th";
  };

  const handleEnterSiteEN = () => {
    // Set cookie with English language info
    setMemorialCookie('en');
    // Redirect to English site
    window.location.href = "/";
  };

  return (
    <>
      <Head>
        <title>Memorial - Jasmine Technology Solution</title>
        <meta name="description" content="Jasmine Technology Solution Memorial Page" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/jts-ico.ico" />
        <link
          rel="preload"
          as="image"
          href="/images/memorial/memorial-desktop-v2.jpg"
          media="(min-width: 769px)"
        />
        <link
          rel="preload"
          as="image"
          href="/images/memorial/memorial-mb-v3.png"
          media="(max-width: 768px)"
        />
      </Head>

      <div className={styles.memorialContainer}>
        {/* Main Content */}
        <main className={styles.mainContent}>
          <div className={styles.memorialContent}>
            <div className={styles.memorialImage}>
              {/* Image Loading Overlay */}
              {isLoading && (
                <div className={styles.imageLoadingOverlay}>
                  <div className={styles.loadingSpinner}></div>
                </div>
              )}
              <img
                src={isMobile ? "/images/memorial/memorial-mb-v3.png" : "/images/memorial/memorial-desktop-v2.jpg"}
                alt="Memorial"
                className={styles.centerImage}
                fetchpriority="high"
                style={{
                  opacity: isLoading ? 0 : 1,
                  transition: 'opacity 1.5s ease-in-out'
                }}
              />
            </div>
          </div>
        </main>

        {/* Footer */}
        
        <footer className={styles.memorialFooter}>
          <div className={styles.footerContainer}>
            <div className={styles.footerContent}>
              {/* Logo on the left */}
              <div className={styles.footerLogo}>
                <Image
                  src="/images/logo/Logo-JTS-Final-min.png"
                  alt="JTS-Brand"
                  width={80}
                  height={36}
                  priority
                />
              </div>

              {/* Copyright in the center */}
              <div className={styles.footerCopyright}>
                <p>
                  &copy; {new Date().getFullYear()}. Jasmine Technology Solution. All rights reserved.
                </p>
              </div>

              {/* Enter Site buttons on the right */}
              <div className={styles.languageButtons}>
                <button
                  className={styles.enterButton}
                  onClick={handleEnterSiteTH}
                >
                  เข้าสู่เว็บไซต์
                </button>
                <button
                  className={styles.enterButton}
                  onClick={handleEnterSiteEN}
                >
                  Enter Site
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export async function getStaticProps() {
  return {
    props: {},
  };
}
